import React, { useReducer } from 'react';
import { SurveyTemplate } from '@Models/SurveyTemplate';
import { MetricConfiguration } from '@Models/MetricConfiguration';
import { SaveState } from '@Models/SaveState';
import { UpdateSurveyTemplateWriteModel } from '@ApiModels';

type State = {
    template: SurveyTemplate,
    saveState: SaveState
};

export type SurveyManagerAction = {
    type: string;
    metricConfiguration?: MetricConfiguration,
    questionIndex?: number,
    saveState?: SaveState,
    stringValue?: string,
    changedFieldName?: keyof UpdateSurveyTemplateWriteModel
}
export enum SurveyManagerActionType {
    MetricToggled = "METRIC_TOGGLED",
    MetricQuestionChanged = "METRIC_QUESTION_CHANGED",
    QuestionTextChanged = "QUESTION_TEXT_CHANGED",
    TestSurveySent = "TEST_SURVEY_SENT",
    SetSaveState = "SET_SAVE_STATE"

}

const emptyState: State = {
    template: new SurveyTemplate('1', '', '', null, [], 0),
    saveState: SaveState.Unchanged
}

type ContextType = State & {
    dispatch: React.Dispatch<SurveyManagerAction>
};

export const SurveyManagerContext = React.createContext<ContextType>({
    ...emptyState,
    dispatch: () => { }
});

function reducer(state: State, action: SurveyManagerAction): State {
    if (!action.metricConfiguration) {
        switch (action.type) {
            case SurveyManagerActionType.SetSaveState: {
                return { ...state, saveState: action.saveState! };
            }
            case SurveyManagerActionType.QuestionTextChanged: {
                switch (action.changedFieldName) {
                    case 'marqueeQuestion':
                        return { ...state, template: state.template.cloneWith(action.stringValue, 'marqueeQuestion') };
                    case 'additionalCommentsQuestion':
                        return { ...state, template: state.template.cloneWith(action.stringValue, 'additionalCommentsQuestion') };
                    default:
                        throw new Error('Invalid survey template field change');
                }
            }
            case SurveyManagerActionType.TestSurveySent: {
                const cloned = state.template.cloneWith(new Date(), 'testSurveySent');
                return { ...state, template: cloned };
            }
            default:
                return state;
        }
    } else {

        const index = state.template.metrics.findIndex(x => x.metric.id === action.metricConfiguration.metric.id);
        if (index < 0) throw new Error('Could not find metric configuration');
        const newMetrics = [...state.template.metrics];

        switch (action.type) {
            case SurveyManagerActionType.MetricToggled: {
                newMetrics[index] = state.template.metrics[index].cloneWithActive(!state.template.metrics[index].active);
                if (newMetrics[index].active === true && !newMetrics[index].questions.some(x => x.enabled === true)) {
                    newMetrics[index].questions = newMetrics[index].questions.map(x => ({ enabled: true, template: x.template }));
                }
                return { ...state, template: state.template.cloneWith(newMetrics, 'metrics') };
            }
            case SurveyManagerActionType.MetricQuestionChanged: {
                const questionIndex = action.questionIndex;
                if (typeof questionIndex === 'undefined') throw new Error('no index supplied');
                const newValue = !state.template.metrics[index].questions[questionIndex].enabled;
                newMetrics[index] = state.template.metrics[index].cloneWithQuestionEnabled(questionIndex, newValue);
                if (!newMetrics[index].questions.some(x => x.enabled === true)) {
                    newMetrics[index].active = false;
                } else if (newValue) {
                    newMetrics[index].active = true;
                }
                return { ...state, template: state.template.cloneWith(newMetrics, 'metrics') };
            }

            default:
                return state;
        }
    }
}

export function SurveyManagerContextProvider(props: { children: JSX.Element[] | JSX.Element, initialState?: State }) {
    const [state, dispatch] = useReducer(reducer, props.initialState || emptyState);
    const value = { ...state, dispatch };
    return <SurveyManagerContext.Provider value={value}>{props.children}</SurveyManagerContext.Provider>;
}
